/**
 * AI Scribe service — async job-based audio processing + live mic text mode.
 *
 * Audio upload flow (async):
 *   1. POST /scribe/jobs → get presigned S3 URL + job_id
 *   2. PUT audio directly to S3 (bypasses API Lambda)
 *   3. Poll GET /scribe/jobs/{id} until completed/failed
 *
 * Live mic flow (sync, unchanged):
 *   POST /visits/{visitId}/ai-assist-text → instant SOAP response
 */
import { apiRequest } from "./api.client";

// ── Response types ──────────────────────────────────────────────────────────

export interface AiScribeMedication {
  drug_name: string;
  dosage: string;
  frequency: string; // OD | BD | TDS | QID | SOS | HS
  timing: string | null; // A/F | B/F | null
  duration_days: number;
  total_quantity: number;
  route: string;
  instructions: string | null;
  allergy_conflict: boolean;
}

export interface AiScribePrescription {
  diagnosis_label: string;
  medications: AiScribeMedication[];
  investigations: string[];
  advice: string | null;
  follow_up: string | null;
}

export interface AiScribeSoapNote {
  chief_complaint: string;
  history_of_present_illness: string;
  review_of_systems: string | null;
  physical_examination: string | null;
  assessment: string;
  plan: string;
}

export interface AiScribeMeta {
  model: string;
  processing_time_s: number;
  audio_size_mb: number;
  s3_key: string | null;
}

export interface AiScribeTranscript {
  original: string;
  hindi: string;
  english: string;
}

export interface AiScribeResponse {
  soap_note: AiScribeSoapNote;
  prescription_document: AiScribePrescription;
  transcript?: AiScribeTranscript;
  meta: AiScribeMeta;
}

// ── Scribe Job types ────────────────────────────────────────────────────────

export interface ScribeJobStart {
  job_id: string;
  upload_url: string;
  s3_key: string;
}

export interface ScribeJobStatus {
  id: string;
  visit_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  transcript: AiScribeTranscript | null;
  soap_note: AiScribeSoapNote | null;
  prescription: AiScribePrescription | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

// ── Async audio upload flow ─────────────────────────────────────────────────

/**
 * Step 1: Create a scribe job — returns presigned S3 upload URL.
 */
export async function createScribeJob(
  visitId: string,
  patientId?: string
): Promise<ScribeJobStart> {
  // Only send patient_id if it's a valid UUID (not a display ID like PAT-XXXXX)
  const isUuid = patientId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(patientId);
  const payload: Record<string, string> = { visit_id: visitId };
  if (isUuid) payload.patient_id = patientId;

  return apiRequest<ScribeJobStart>("/scribe/jobs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Step 2: Upload audio directly to S3 using presigned URL.
 * Bypasses the API Lambda entirely — keeps API fast.
 */
export async function uploadAudioToS3(
  presignedUrl: string,
  audioBlob: Blob
): Promise<void> {
  const resp = await fetch(presignedUrl, {
    method: "PUT",
    body: audioBlob,
    headers: { "Content-Type": "audio/webm" },
    signal: AbortSignal.timeout(300_000), // 5 min for large files
  });
  if (!resp.ok) {
    throw new Error(`S3 upload failed: ${resp.status} ${resp.statusText}`);
  }
}

/**
 * Step 3: Poll job status until completed or failed.
 */
export async function getScribeJobStatus(
  jobId: string
): Promise<ScribeJobStatus> {
  return apiRequest<ScribeJobStatus>(`/scribe/jobs/${jobId}`);
}

/**
 * Poll until job completes. Returns the final job status.
 * Calls onStatusChange for each poll so the UI can update.
 */
export async function pollScribeJob(
  jobId: string,
  opts?: {
    intervalMs?: number;
    maxWaitMs?: number;
    onStatusChange?: (status: ScribeJobStatus) => void;
  }
): Promise<ScribeJobStatus> {
  const interval = opts?.intervalMs ?? 3000;
  const maxWait = opts?.maxWaitMs ?? 900_000; // 15 min max
  const start = Date.now();

  while (Date.now() - start < maxWait) {
    const job = await getScribeJobStatus(jobId);
    opts?.onStatusChange?.(job);

    if (job.status === "completed" || job.status === "failed") {
      return job;
    }

    await new Promise((r) => setTimeout(r, interval));
  }

  throw new Error("Scribe job timed out — processing took too long.");
}

/**
 * Convenience: full async scribe flow in one call.
 * Creates job → uploads to S3 → polls until done.
 *
 * NOTE: Requires Worker Lambda deployed via CDK. If not deployed,
 * jobs stay "pending" forever. Use runAiScribe() (sync) as fallback.
 *
 * Auto-detects: tries async first, falls back to sync if scribe/jobs
 * endpoint returns 404 (not deployed) or job stays pending > 30s.
 */
export async function runAiScribeAsync(
  visitId: string,
  patientId: string,
  audioBlob: Blob,
  onStatusChange?: (status: ScribeJobStatus) => void
): Promise<ScribeJobStatus> {
  // 1. Create job
  const { job_id, upload_url } = await createScribeJob(visitId, patientId);

  // 2. Upload audio to S3
  await uploadAudioToS3(upload_url, audioBlob);

  // 3. Poll for results
  return pollScribeJob(job_id, { onStatusChange });
}

// ── Legacy sync API (kept for backward compat, will be removed) ─────────────

export async function runAiScribe(
  visitId: string,
  audioFile: Blob | File,
  filename?: string
): Promise<AiScribeResponse> {
  const form = new FormData();
  form.append("audio", audioFile, filename || "recording.webm");

  return apiRequest<AiScribeResponse>(
    `/visits/${visitId}/ai-assist`,
    { method: "POST", body: form, signal: AbortSignal.timeout(180_000) }
  );
}

/**
 * Live Mic mode — send transcript text to Gemini for SOAP structuring.
 * This stays synchronous (text-only, fast, no ffmpeg needed).
 */
export async function runAiScribeFromText(
  visitId: string,
  transcript: string
): Promise<AiScribeResponse> {
  return apiRequest<AiScribeResponse>(
    `/visits/${visitId}/ai-assist-text`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transcript }),
      signal: AbortSignal.timeout(120_000),
    }
  );
}
